'use client'

import React from 'react'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'
import { Avatar } from '@/components/ui/avatar'
import type { ChatMessage as ChatMessageType } from '@/context/AIContext'

interface ChatMessageProps {
  message: ChatMessageType
  userName?: string
  userImage?: string | null
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

/**
 * Render inline markdown (bold, code, italic) in text
 */
function renderInlineMarkdown(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = []
  // Match **bold**, `code`, and *italic*
  const regex = /(\*\*[^*]+\*\*)|(`[^`]+`)|(\*[^*]+\*)/g
  let lastIndex = 0
  let match
  let keyIndex = 0

  while ((match = regex.exec(text)) !== null) {
    // Add text before match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }

    const segment = match[0]
    if (segment.startsWith('**') && segment.endsWith('**')) {
      // Bold
      parts.push(
        <strong key={keyIndex++} className="font-semibold text-foreground">
          {segment.slice(2, -2)}
        </strong>
      )
    } else if (segment.startsWith('`') && segment.endsWith('`')) {
      // Code
      parts.push(
        <code
          key={keyIndex++}
          className="px-1.5 py-0.5 bg-muted/50 rounded text-xs font-mono text-brand-navy"
        >
          {segment.slice(1, -1)}
        </code>
      )
    } else if (segment.startsWith('*') && segment.endsWith('*')) {
      // Italic
      parts.push(
        <em key={keyIndex++} className="italic">
          {segment.slice(1, -1)}
        </em>
      )
    }
    lastIndex = regex.lastIndex
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return parts.length > 0 ? parts : [text]
}

/**
 * Parse a markdown table row into cells
 */
function parseTableRow(row: string): string[] {
  return row
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim())
}

/**
 * Check if a line is a table alignment row (e.g. | :--- | :---: | ---: |)
 */
function isAlignmentRow(line: string): boolean {
  return /^\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(line.trim())
}

/**
 * Get column alignment from an alignment row
 */
function getAlignments(line: string): ('left' | 'center' | 'right')[] {
  return parseTableRow(line).map((cell) => {
    const trimmed = cell.trim()
    if (trimmed.startsWith(':') && trimmed.endsWith(':')) return 'center'
    if (trimmed.endsWith(':')) return 'right'
    return 'left'
  })
}

/**
 * Pre-process lines into blocks: tables are grouped, everything else is individual
 */
type Block =
  | { type: 'table'; rows: string[]; startIndex: number }
  | { type: 'line'; content: string; index: number }

function groupBlocks(lines: string[]): Block[] {
  const blocks: Block[] = []
  let i = 0
  while (i < lines.length) {
    const trimmed = lines[i].trim()
    if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      // Start of a table block — collect all consecutive pipe-lines
      const tableRows: string[] = []
      const startIndex = i
      while (i < lines.length) {
        const t = lines[i].trim()
        if (t.startsWith('|') && t.includes('|', 1)) {
          tableRows.push(t)
          i++
        } else {
          break
        }
      }
      blocks.push({ type: 'table', rows: tableRows, startIndex })
    } else {
      blocks.push({ type: 'line', content: lines[i], index: i })
      i++
    }
  }
  return blocks
}

/**
 * Render a markdown table block as a styled HTML table
 */
function MarkdownTable({ rows, keyPrefix }: { rows: string[]; keyPrefix: number }) {
  if (rows.length < 2) return null

  const headerCells = parseTableRow(rows[0])
  let alignments: ('left' | 'center' | 'right')[] = headerCells.map(() => 'left')
  let bodyStartIndex = 1

  // If the second row is an alignment row, parse alignments and skip it
  if (rows.length >= 2 && isAlignmentRow(rows[1])) {
    alignments = getAlignments(rows[1])
    bodyStartIndex = 2
  }

  const bodyRows = rows.slice(bodyStartIndex)

  return (
    <div key={`table-${keyPrefix}`} className="my-2 overflow-x-auto rounded-lg border border-white/[0.06]">
      <table className="w-full border-collapse text-[13px]">
        <thead>
          <tr className="bg-[rgba(20,26,66,0.6)]">
            {headerCells.map((cell, ci) => (
              <th
                key={ci}
                className="px-3 py-2 text-left font-semibold text-[#E7E7E7] border-b border-[rgba(138,162,223,0.2)] whitespace-nowrap"
                style={{ textAlign: alignments[ci] || 'left' }}
              >
                {renderInlineMarkdown(cell)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {bodyRows.map((row, ri) => {
            const cells = parseTableRow(row)
            return (
              <tr
                key={ri}
                className={cn(
                  'transition-colors',
                  ri % 2 === 0 ? 'bg-transparent' : 'bg-white/[0.02]',
                  'hover:bg-white/[0.05]',
                )}
              >
                {cells.map((cell, ci) => (
                  <td
                    key={ci}
                    className="px-2 py-1.5 text-muted-foreground border-b border-white/[0.06]"
                    style={{ textAlign: alignments[ci] || 'left' }}
                  >
                    {renderInlineMarkdown(cell)}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Render a single non-table line
 */
function renderLine(trimmedLine: string, key: number): React.ReactNode {
  // Empty line - add spacing
  if (!trimmedLine) {
    return <div key={key} className="h-1" />
  }

  // Headers (##, ###)
  if (trimmedLine.startsWith('### ')) {
    return (
      <h4 key={key} className="font-semibold text-foreground mt-3 first:mt-0">
        {renderInlineMarkdown(trimmedLine.slice(4))}
      </h4>
    )
  }
  if (trimmedLine.startsWith('## ')) {
    return (
      <h3 key={key} className="font-bold text-foreground mt-3 first:mt-0">
        {renderInlineMarkdown(trimmedLine.slice(3))}
      </h3>
    )
  }

  // Blockquote
  if (trimmedLine.startsWith('> ')) {
    return (
      <blockquote
        key={key}
        className="pl-3 border-l-2 border-brand-cornflower/50 text-muted-foreground italic"
      >
        {renderInlineMarkdown(trimmedLine.slice(2))}
      </blockquote>
    )
  }

  // Bullet points (* or -)
  if (/^[*\-]\s/.test(trimmedLine)) {
    return (
      <div key={key} className="flex gap-2 pl-1">
        <span className="w-1.5 h-1.5 rounded-full bg-brand-cornflower/60 mt-2 flex-shrink-0" />
        <span>{renderInlineMarkdown(trimmedLine.replace(/^[*\-]\s/, ''))}</span>
      </div>
    )
  }

  // Numbered lists
  if (/^\d+\.\s/.test(trimmedLine)) {
    const match = trimmedLine.match(/^(\d+)\.\s(.*)/)
    if (match) {
      return (
        <div key={key} className="flex gap-2 pl-1">
          <span className="text-brand-cornflower font-medium min-w-[1.25rem]">
            {match[1]}.
          </span>
          <span>{renderInlineMarkdown(match[2])}</span>
        </div>
      )
    }
  }

  // Regular paragraph
  return <p key={key}>{renderInlineMarkdown(trimmedLine)}</p>
}

/**
 * Render markdown content with support for headers, lists, blockquotes, and tables
 */
function MarkdownContent({ content }: { content: string }) {
  const lines = content.split('\n')
  const blocks = groupBlocks(lines)

  return (
    <div className="space-y-2 text-sm leading-relaxed">
      {blocks.map((block) => {
        if (block.type === 'table') {
          return <MarkdownTable key={`t-${block.startIndex}`} rows={block.rows} keyPrefix={block.startIndex} />
        }
        return renderLine(block.content.trim(), block.index)
      })}
    </div>
  )
}

export function ChatMessage({ message, userName, userImage }: ChatMessageProps) {
  const isUser = message.role === 'user'
  const isLoading = message.isLoading

  return (
    <div
      className={cn(
        'flex gap-3',
        isUser ? 'flex-row-reverse' : 'flex-row'
      )}
    >
      {/* Avatar */}
      <div className="flex-shrink-0">
        {isUser ? (
          <Avatar
            src={userImage}
            fallback={userName || 'U'}
            size="sm"
          />
        ) : (
          <div className={cn(
            'flex h-8 w-8 items-center justify-center rounded-full',
            'bg-gradient-to-br from-brand-navy to-brand-purple',
            'shadow-soft'
          )}>
            <Icons.sparkles className="h-4 w-4 text-white" strokeWidth={1.5} />
          </div>
        )}
      </div>

      {/* Message Content */}
      <div className={cn(
        'flex flex-col gap-1',
        isUser ? 'max-w-[85%]' : 'max-w-[95%]',
        isUser ? 'items-end' : 'items-start'
      )}>
        <div
          className={cn(
            'rounded-2xl px-4 py-3',
            'transition-all duration-200',
            isUser
              ? 'bg-brand-navy text-white rounded-br-md'
              : 'bg-white/90 text-foreground border border-border/50 rounded-bl-md shadow-soft',
            isLoading && 'animate-pulse'
          )}
        >
          {isLoading ? (
            <div className="flex items-center gap-2 py-1">
              <div className="flex gap-1">
                <span className="h-2 w-2 animate-bounce rounded-full bg-brand-muted/60" style={{ animationDelay: '0ms' }} />
                <span className="h-2 w-2 animate-bounce rounded-full bg-brand-muted/60" style={{ animationDelay: '150ms' }} />
                <span className="h-2 w-2 animate-bounce rounded-full bg-brand-muted/60" style={{ animationDelay: '300ms' }} />
              </div>
              <span className="text-sm text-brand-muted">Thinking...</span>
            </div>
          ) : isUser ? (
            <p className="text-sm whitespace-pre-wrap leading-relaxed">
              {message.content}
            </p>
          ) : (
            <MarkdownContent content={message.content} />
          )}
        </div>

        {/* Timestamp */}
        <span className={cn(
          'text-[10px] text-muted-foreground/60',
          isUser ? 'pr-1' : 'pl-1'
        )}>
          {formatTime(message.timestamp)}
        </span>

        {/* Tool calls — show download links and actions */}
        {message.toolCalls && message.toolCalls.length > 0 && (
          <div className="flex flex-col gap-1.5 pl-1 mt-1">
            {message.toolCalls.map((tc) => {
              const result = tc.result as Record<string, unknown> | undefined

              // Report download button (authenticated blob download)
              if (tc.name === 'generate_report' && result?.download_url) {
                const handleDownload = async () => {
                  try {
                    const { getSession } = await import('next-auth/react')
                    const session = await getSession()
                    const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8001'
                    const basePath = process.env.NEXT_PUBLIC_BASE_PATH || ''
                    const downloadUrl = `${apiUrl}${basePath}${result.download_url}`

                    const response = await fetch(downloadUrl, {
                      headers: session?.accessToken
                        ? { Authorization: `Bearer ${session.accessToken}` }
                        : {},
                    })

                    if (!response.ok) throw new Error(`Download failed: ${response.status}`)

                    const blob = await response.blob()
                    const url = URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = `report.${result.format || 'pdf'}`
                    document.body.appendChild(a)
                    a.click()
                    document.body.removeChild(a)
                    URL.revokeObjectURL(url)
                  } catch {
                    window.open(
                      `${process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8001'}${
                        process.env.NEXT_PUBLIC_BASE_PATH || ''
                      }${result.download_url}`,
                      '_blank',
                    )
                  }
                }
                return (
                  <button
                    key={tc.id}
                    type="button"
                    onClick={handleDownload}
                    className={cn(
                      'inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium',
                      'bg-brand-cornflower/10 text-brand-cornflower border border-brand-cornflower/20',
                      'hover:bg-brand-cornflower/20 transition-colors cursor-pointer',
                    )}
                  >
                    <Icons.download className="h-3.5 w-3.5" strokeWidth={1.5} />
                    Download {String(result.format || 'PDF').toUpperCase()} Report
                  </button>
                )
              }

              // Navigate action
              if (tc.name === 'navigate_user' && result?.target_page) {
                return (
                  <a
                    key={tc.id}
                    href={String(result.target_page)}
                    className={cn(
                      'inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium',
                      'bg-brand-purple/10 text-brand-purple border border-brand-purple/20',
                      'hover:bg-brand-purple/20 transition-colors',
                    )}
                  >
                    <Icons.externalLink className="h-3.5 w-3.5" strokeWidth={1.5} />
                    Go to {String(result.page_name || result.target_page)}
                  </a>
                )
              }

              // Default: subtle tool indicator
              return (
                <div key={tc.id} className="flex items-center gap-1.5 text-[10px] text-brand-cornflower">
                  <Icons.zap className="h-3 w-3" strokeWidth={1.5} />
                  <span>{tc.name.replace(/_/g, ' ')}</span>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
